import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Home, Gem, Sparkles, Globe, GraduationCap, PenLine, Calendar, User, Shield } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { isAdminEmail } from '../lib/admin';

export default function Layout({ children, user }) {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const isAdmin = isAdminEmail(user?.email);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'hr' : 'en';
    i18n.changeLanguage(newLang);
    localStorage.setItem('language', newLang);
  };

  const isQuranSection = location.pathname.startsWith('/quran') || location.pathname === '/hadith';

  const navItems = [
    { path: '/dashboard', icon: Home, label: t('dashboard') },
    { path: '/quran', icon: GraduationCap, label: 'Ilm' },
    { path: '/dhikr', icon: Sparkles, label: i18n.language === 'hr' ? 'Zikr' : 'Dhikr' },
    { path: '/bereket', icon: Gem, label: i18n.language === 'hr' ? 'Bereket' : 'Barakah' },
    { path: '/journal', icon: PenLine, label: i18n.language === 'hr' ? 'Dnevnik' : 'Journal' },
    { path: '/planner', icon: Calendar, label: i18n.language === 'hr' ? 'Planer' : 'Planner' },
  ];

  const mobileNavItems = [
    { path: '/dashboard', icon: Home, label: t('dashboard') },
    { path: '/quran', icon: GraduationCap, label: 'Ilm' },
    { path: '/bereket', icon: Gem, label: i18n.language === 'hr' ? 'Bereket' : 'Barakah' },
    { path: '/profile', icon: User, label: i18n.language === 'hr' ? 'Profil' : 'Profile' },
  ];

  return (
    <div className="min-h-screen bg-[#F5F5F4] dark:bg-[#141210] grain-texture transition-colors duration-300">
      <header className="hidden md:block bg-white/80 dark:bg-[#1a1614]/90 backdrop-blur-sm border-b border-stone-200/60 dark:border-stone-700/40 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center shadow-sm">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-heading font-semibold text-[#292524] dark:text-stone-100 tracking-tight">
                {t('app_name')}
              </h1>
            </Link>

            <nav className="flex items-center gap-1 bg-stone-100/50 dark:bg-stone-800/50 p-1 rounded-full border border-stone-200/50 dark:border-stone-700/50">
              {navItems.map((item) => {
                const isActive = item.path === '/quran'
                  ? isQuranSection
                  : location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full font-medium transition-all duration-300 ${
                      isActive
                        ? 'bg-primary text-white shadow-md transform scale-105'
                        : 'text-[#57534E] dark:text-stone-400 hover:bg-white dark:hover:bg-stone-700 hover:text-[#292524] dark:hover:text-stone-200 hover:shadow-sm'
                    }`}
                  >
                    <item.icon className="w-4 h-4" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </nav>

            <div className="flex items-center gap-2">
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-2 px-3 py-2 rounded-full hover:bg-stone-100 dark:hover:bg-stone-700 transition-all text-[#57534E] dark:text-stone-400 border border-transparent hover:border-stone-200 dark:hover:border-stone-600"
                title={t('language')}
              >
                <Globe className="w-4 h-4" />
                <span className="text-sm font-medium">
                  {i18n.language.toUpperCase()}
                </span>
              </button>
              <Link
                to="/profile"
                className={`flex items-center gap-2 px-4 py-2 rounded-full font-medium text-sm transition-all ${
                  location.pathname === '/profile'
                    ? 'bg-primary text-white shadow-md'
                    : 'bg-stone-100 dark:bg-stone-700 text-[#292524] dark:text-stone-200 hover:bg-stone-200 dark:hover:bg-stone-600'
                }`}
                data-testid="layout-profile-link"
              >
                <User className="w-4 h-4" />
                <span>{i18n.language === 'hr' ? 'Profil' : 'Profile'}</span>
              </Link>
              {isAdmin && (
                <Link
                  to="/admin"
                  className={`flex items-center gap-2 px-4 py-2 rounded-full font-medium text-sm transition-all ${
                    location.pathname === '/admin'
                      ? 'bg-primary text-white shadow-md'
                      : 'bg-stone-100 dark:bg-stone-700 text-[#292524] dark:text-stone-200 hover:bg-stone-200 dark:hover:bg-stone-600'
                  }`}
                  data-testid="layout-admin-link"
                >
                  <Shield className="w-4 h-4" />
                  <span>Admin</span>
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="pb-24 md:pb-12 pt-4 md:pt-8 min-h-[calc(100vh-80px)]">{children}</main>

      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white/95 dark:bg-[#1a1614]/95 backdrop-blur-xl border-t border-stone-200/60 dark:border-stone-700/40 pb-safe z-50 shadow-[0_-4px_20px_rgba(0,0,0,0.05)] dark:shadow-[0_-4px_20px_rgba(0,0,0,0.3)]">
        <div className="flex justify-around items-center h-16 px-2">
          {mobileNavItems.map((item) => {
            const isActive = item.path === '/quran'
              ? isQuranSection
              : location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center justify-center w-full h-full gap-1 transition-all duration-300 ${
                  isActive ? 'text-primary' : 'text-stone-400 dark:text-stone-500 hover:text-stone-600 dark:hover:text-stone-300'
                }`}
              >
                <div className={`p-1.5 rounded-xl transition-all duration-300 ${
                  isActive ? 'bg-primary/10 translate-y-[-2px]' : 'bg-transparent'
                }`}>
                  <item.icon className={`w-5 h-5 ${isActive ? 'fill-current' : ''}`} />
                </div>
                <span className={`text-[10px] font-medium tracking-wide transition-all ${
                  isActive ? 'text-primary font-bold' : ''
                }`}>
                  {item.label}
                </span>
              </Link>
            );
          })}
          {isAdmin && (
            <Link
              to="/admin"
              className={`flex flex-col items-center justify-center w-full h-full gap-1 transition-all duration-300 ${
                location.pathname === '/admin' ? 'text-primary' : 'text-stone-400 dark:text-stone-500 hover:text-stone-600 dark:hover:text-stone-300'
              }`}
              data-testid="layout-admin-mobile-link"
            >
              <div className={`p-1.5 rounded-xl transition-all duration-300 ${location.pathname === '/admin' ? 'bg-primary/10 translate-y-[-2px]' : 'bg-transparent'}`}>
                <Shield className={`w-5 h-5 ${location.pathname === '/admin' ? 'fill-current' : ''}`} />
              </div>
              <span className={`text-[10px] font-medium tracking-wide transition-all ${location.pathname === '/admin' ? 'text-primary font-bold' : ''}`}>Admin</span>
            </Link>
          )}
        </div>
      </nav>
    </div>
  );
}
